import React, { useState, useEffect, useMemo } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from 'reactstrap'
import Select from 'react-select'
import { useMutation, useQuery } from '@apollo/client'
import { ADD_INBOX_MEMBER, UPDATE_INBOX_MEMBER } from './Mutations'
import { GET_ADMIN_INBOXES, GET_ALL_USERS } from './Queries'
import { RepsTypeAhead } from '../../TaskManagement/TaskComponents/TypeAheadComponents'

const MEMBER_ROLES = [
    { value: 'admin', label: 'Admin' },
    { value: 'member', label: 'Member' }
]

export default function AddEditInboxMemberModal({ isOpen, toggle, inboxMember, onSuccess }) {
    const [inboxId, setInboxId] = useState('')
    const [userIds, setUserIds] = useState([])
    const [userId, setUserId] = useState('')
    const [role, setRole] = useState('member')
    const [canBeAssigned, setCanBeAssigned] = useState(true)
    const [userSearch, setUserSearch] = useState('')
    const [errors, setErrors] = useState([])

    const isEdit = !!inboxMember

    const { data: inboxesData } = useQuery(GET_ADMIN_INBOXES, {
        variables: { isArchived: false }
    })

    const { data: usersData, loading: usersLoading } = useQuery(GET_ALL_USERS, {
        variables: {
            first: 50,
            username: userSearch || undefined,
            email: userSearch || undefined
        },
        skip: isEdit
    })

    const inboxes = useMemo(
        () => inboxesData?.myAdminInboxes?.edges?.map(edge => edge.node) || [],
        [inboxesData]
    )

    const users = useMemo(
        () =>
            usersData?.allMembers?.edges
                ?.map(edge => edge.node)
                ?.filter(node => node?.user) || [],
        [usersData]
    )

    useEffect(() => {
        if (inboxMember) {
            setInboxId(inboxMember.inbox?.id || '')
            setUserId(inboxMember.user?.id || '')
            setRole(inboxMember.role || 'member')
            setCanBeAssigned(inboxMember.canBeAssigned !== false)
        } else {
            setInboxId(inboxes.length > 0 ? inboxes[0].id : '')
            setUserId('')
            setRole('member')
            setCanBeAssigned(true)
            setUserSearch('')
        }
        setErrors([])
    }, [inboxMember, isOpen, inboxes])

    const [addInboxMember, { loading: adding }] = useMutation(ADD_INBOX_MEMBER, {
        onCompleted: (data) => {
            if (data.messagingAddInboxMember.ok) {
                toggle()
                if (onSuccess) {
                    onSuccess(`Users added to inbox.`)
                }
            } else {
                setErrors(data.messagingAddInboxMember.errors || ['Failed to add member'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const [updateInboxMember, { loading: updating }] = useMutation(UPDATE_INBOX_MEMBER, {
        onCompleted: (data) => {
            if (data.messagingUpdateInboxMember.ok) {
                toggle()
                if (onSuccess) {
                    const updatedMember = data.messagingUpdateInboxMember.inboxMember
                    const displayName = updatedMember?.user?.username || 'Member'
                    onSuccess(`${displayName}'s permissions have been updated.`)
                }
            } else {
                setErrors(data.messagingUpdateInboxMember.errors || ['Failed to update member'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        setErrors([])

        if (isEdit) {
            updateInboxMember({
                variables: {
                    id: inboxMember.id,
                    role,
                    canBeAssigned
                }
            })
        } else {
            if (!inboxId) {
                setErrors(['Please select an inbox'])
                return
            }
            if (!userIds) {
                setErrors(['Please select a user'])
                return
            }

            addInboxMember({
                variables: {
                    inboxId,
                    userIds,
                    role,
                    canBeAssigned
                }
            })
        }
    }

    const loading = adding || updating

    return (
        <Modal isOpen={isOpen}>
            <ModalHeader>
                {isEdit ? 'Edit Inbox Member' : 'Add Inbox Member'}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {errors.length > 0 && (
                        <Alert color="danger">
                            {errors.map((error, idx) => (
                                <div key={idx}>{error}</div>
                            ))}
                        </Alert>
                    )}

                    {!isEdit && (
                        <>
                            <FormGroup>
                                <Label for="memberInbox">Inbox *</Label>
                                <Select className="bos-custom-select"
                                    classNamePrefix="bos-select"
                                    isLoading={false}
                                    options={
                                        inboxes.map((inbox) => ({ value: inbox.id, label: inbox.name }))
                                    }
                                    placeholder="Select inbox for channel"
                                    isMulti={false}
                                    onChange={(option) => setInboxId(option.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="userIds">Users to add</Label>
                                <RepsTypeAhead onChange={(selectedUserIds) => { setUserIds(selectedUserIds) }} />
                            </FormGroup>
                        </>
                    )}

                    {isEdit && (
                        <FormGroup>
                            <Label>User</Label>
                            <Input
                                type="text"
                                value={`${inboxMember.user?.username || ''} (${inboxMember.user?.email || ''})`}
                                disabled
                            />
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label for="memberRole">Role *</Label>
                        <Select className="bos-custom-select"
                            classNamePrefix="bos-select"
                            isLoading={false}
                            options={MEMBER_ROLES}
                            placeholder="Select member role"
                            isMulti={false}
                            value={MEMBER_ROLES.find(r => r.value === role)}
                            onChange={(option) => setRole(option.value)}
                        />
                    </FormGroup>

                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={canBeAssigned}
                                onChange={(e) => setCanBeAssigned(e.target.checked)}
                                disabled={loading}
                            />
                            Can be assigned conversations
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving...' : isEdit ? 'Update' : 'Add'}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}
